import { ActiveFilters, Filter } from '../models/filters';
import { Tender, TenderAnswer, Tendering, TenderingPart } from '../models/tendering';
import { deepCloneObject } from './clone';
import { setTenderListRankings, sortByRank } from './ranking';

export function initTenderList(
  unfilteredList: Tender[],
  tendering: Tendering,
  activeFilters: ActiveFilters): Tender[] {

  let tenderList: Tender[] = [];

  for (const tender of unfilteredList) {
    // Dont add hidden tenders
    if (!tender.tenderProperties.hidden && !tender.tenderProperties.unpublished) {
      const tenderListObject: Tender = tender;
      
      // Set contact object
      if (!tenderListObject.contact) { tenderListObject.contact = {}; }
      if (!tenderListObject.contact.email) { 
        tenderListObject.contact.email = tender.tenderAnswers?.find(a => a.questionID === tendering.properties.contact.email)?.value ?? ''; 
      }
      if (!tenderListObject.contact.phone) { 
        tenderListObject.contact.phone = tender.tenderAnswers?.find(a => a.questionID === tendering.properties.contact.phone)?.value ?? ''; 
      }
      
      for (const part of tendering.tenderingParts) {
        // Dont add tendering parts that is not evaluated for ranking
        if (part.evaluated) {
          let price;
          // If double price
          if (typeof part.ranking === 'object' && part.ranking !== null) {
            const price1 = tender.tenderAnswers?.find(a => a.questionID === part.ranking.prices[0]);
            const price2 = tender.tenderAnswers?.find(a => a.questionID === part.ranking.prices[1]);
            if (price1 && price2) {
              price = {priceInit: price1.value, priceExt: price2.value, value: 0};
              price.value = (Number(price1.value) * 12) + (Number(price2.value) * 24);
            }
          }
          else {
            price = tender.tenderAnswers?.find(a => a.questionID === part.ranking);
          }

          if (price) {
            for (const tpart of tender.tenderingParts) {
              if (part.tenderingPartID === tpart.tenderingPartID ) {
                tpart.price = Number(price.value);
                if (price.priceInit && price.priceExt) {
                  tpart.priceInit = Number(price.priceInit);
                  tpart.priceExt = Number(price.priceExt);
                }
              }
            }
          }
        }
      }

      tenderList.push(tenderListObject);
    }
  }

  // Apply tenderingPart
  if (activeFilters.tenderingPart) {
    const tpart = tendering.tenderingParts.find(tp => tp.tenderingPartID === activeFilters.tenderingPart?.tenderingPartID);
    if (!tpart) {
      return tenderList;
    }
    tenderList = filterByTenderingPart(tenderList, tpart);
  }

  // Apply basefilters
  tenderList = filterTenderList(tenderList, activeFilters.baseFilters);

  return tenderList;
}

export function filterByTenderingPart(tenderList: Tender[], part: TenderingPart): Tender[] {
  let tenderListByPart = tenderList.filter(tl => {
    let found = false;
    for (const tp of tl.tenderingParts) {
      if (tp.price > 0 && tp.tenderingPartID === part.tenderingPartID){
        found = true;
        tl.price = Number(tp.price);
        if (tp.priceInit) { tl.priceInit = Number(tp.priceInit); }
        if (tp.priceExt) { tl.priceExt = Number(tp.priceExt); }
        break;
      }
    }
    return found;
  });

  tenderListByPart = setTenderListRankings(tenderListByPart, part.rankings);
  tenderListByPart = sortByRank(tenderListByPart);

  return tenderListByPart;
}

export function filterTenderList(tenderList: Tender[], filters: any[]): any[] {
  let tenderListResult = deepCloneObject(tenderList);
  for (const tender of tenderListResult) {
    for (const filter of filters) {
      let answer;
      let answerMin;
      let answerMax;

      if (filter.type === 'int-range') {
        answerMin = tender.tenderAnswers.find((answr: TenderAnswer) => {
          return answr.questionID === filter.minQuestionID;
        });
        answerMax = tender.tenderAnswers.find((answr: TenderAnswer) => {
          return answr.questionID === filter.maxQuestionID;
        });
      }
      else {
        answer = tender.tenderAnswers.find((answr: TenderAnswer) => {
          return answr.questionID === filter.questionID;
        });
      }

      switch (filter.type) {
        case 'text':
          if (!textContainsValueFilter(filter, answer)){
            tender.remove = true;
          }
          break;
        case 'select':
          if(filter.questionType == 'text'){
            if (!textValueFilter(filter, answer)){
              tender.remove = true;
            }
          }
          if(filter.questionType == 'bool'){
            if(!answer || answer.value == 0){
              tender.remove = true;
            }
          }
          break;
        case 'bool':
          if (!boolFilter(filter, answer)){
            tender.remove = true;
          }
          break;
        case 'radio-bool':
          if (!boolFilter(filter, answer)){
            tender.remove = true;
          }
          break;
        case 'int':
          if (!intFilter(filter, answer)){
            tender.remove = true;
          }
          break;
        case 'int-range':
          if (!intRangeFilter(filter, answerMin, answerMax)){
            tender.remove = true;
          }
          break;
        default:
          break;
      }

      if (tender.remove) {
        break;
      }
    }
  }

  tenderListResult = tenderListResult.filter((t: Tender) => !t.remove);
  tenderListResult.forEach((element: Tender) => {
    element.remove = undefined;
  });

  tenderListResult = sortByRank(tenderListResult);

  tenderListResult = tenderListResult.map((tender: Tender) => ({ ...tender, rankingTarget: false }));
  const firstNonDeniedIndex = tenderListResult.findIndex((t: Tender) => !t.denied);
  if (firstNonDeniedIndex >= 0) {
    tenderListResult[firstNonDeniedIndex].rankingTarget = true;
  }

  return tenderListResult;
}

function textContainsValueFilter(filter: Filter, answer: TenderAnswer): boolean {
  const filters = filter.value.toLowerCase().trim().split(' ');
  let found = false;
  
  for (let i = 0; i < filters.length; i++) {
    if (answer.value.toLowerCase().trim().includes(filters[i].trim())) {
      found = true;
    }
  }

  return found;
}

function textValueFilter(filter: Filter, answer: TenderAnswer): boolean {
  if (answer.value.toLowerCase().trim().includes(filter.value.toLowerCase().trim())) {
    return true;
  }
  else {
    return false;
  }
}

function boolFilter(filter: Filter, answer: TenderAnswer): boolean {
  if (answer && filter.value == answer.value) {
    return true;
  }
  else {
    return false;
  }
}

function intFilter(filter: Filter, answer: TenderAnswer): boolean {
  if (Number(filter.value) === Number(answer.value)) {
    return true;
  }
  else {
    return false;
  }
}

function intRangeFilter(filter: Filter, answerMin: TenderAnswer, answerMax: TenderAnswer): boolean {
  if (Number(filter.value) >= Number(answerMin.value) && Number(filter.value) <= Number(answerMax.value)) {
    return true;
  }
  else {
    return false;
  }
}
